import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {gsap, Linear} from "gsap";
import {CustomWiggle} from "gsap/dist/CustomWiggle";
import {BruteForce} from "../BruteForce";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {FontWeight} from "@nolimitcity/slot-launcher/bin/loader/font/FontStatics";
import {BruteForceGameConfig} from "../BruteForceGameConfig";
import {TimelineSprite} from "@nolimitcity/promo-panel/bin/utils/TimelineSprite";
import {BLEND_MODES} from "pixi.js";
import {BruteForceSound} from "../sound/BruteForceSound";

export class BruteForceGameView extends PIXI.Container implements StageLayer {

    private _nudgeCounterScreen: PIXI.spine.Spine;
    private _nudgeMultiplierText: PIXI.Text | null;
    private _nudgeMultiplierValue: number;
    private _textShowing: boolean;
    private _trailLandscapeRotation: number[] = [1.04, 0.83, 0.62, 0.55, 0.48];
    private _trailPortraitRotation: number[] = [1.5, 1.2, 1, 0.8, 0.7];


    constructor() {
        super();
        this.name = "BruteForceGameContainer";

        this._nudgeMultiplierValue = 0;
        this._textShowing = false;

        this._nudgeCounterScreen = new PIXI.spine.Spine(BruteForce.assetLoader.getSpineAsset("nudgeCounterScreen"));
        this._nudgeCounterScreen.position.set(-101, 230);
        this._nudgeCounterScreen.scale.set(1, 1);
        this._nudgeCounterScreen.name = "nudgeCounterScreen";
        this._nudgeCounterScreen.skeleton.setSkinByName("normal_port");
        this._nudgeCounterScreen.state.setAnimation(0, "normal", false);

        this.onOrientationChanged();
    }

    get isTotalNudgeTextVisible(): boolean {
        return this._textShowing;
    }

    get currentTotalMultiplierValue(): number {
        return this._nudgeMultiplierValue;
    }

    set currentTotalMultiplierValue(numValue: number) {
        this._nudgeMultiplierValue = numValue;
    }

    onOrientationChanged() {
        if (NolimitApplication.isLandscape) {
            this._nudgeCounterScreen.skeleton.setSkinByName(this.isBoosterOn() ? "feture_port" : "normal_port");
            this._nudgeCounterScreen.position.set(-101, 230);
            if (this._nudgeMultiplierText) {
                this._nudgeMultiplierText.pivot.x = this._nudgeMultiplierText!.width * 0.5;
                this._nudgeMultiplierText.position.set(0, -15);
            }
        } else {
            this._nudgeCounterScreen.skeleton.setSkinByName(this.isBoosterOn() ? "feture_land" : "normal_land");
            this._nudgeCounterScreen.position.set(75, -25);
            if (this._nudgeMultiplierText) {
                this._nudgeMultiplierText.position.set(2, -20);
                this._nudgeMultiplierText.pivot.x = this._nudgeMultiplierText!.width * 0.5;
            }
        }
    }

    public screenShake(dur: number, wiggles: number = 40, isVertical: boolean, movepos?: number): gsap.core.Timeline {
        let moveXVal: number = movepos ? movepos : 3,
            moveYVal: number = movepos ? movepos : 3;
        let wiggleTl: gsap.core.Timeline = gsap.timeline(),
            x: string = Math.random() > 0.5 ? `-=${moveXVal}` : `+=${moveXVal}`,
            y: string = Math.random() > 0.5 ? `-=${moveYVal}` : `-=${moveYVal}`;

        const shakeWiggle = CustomWiggle.create("shakeWiggle", {
            wiggles: wiggles,
            amplitudeEase: Linear.easeNone,
            timingEase: Linear.easeNone
        });
        if (!isVertical) {
            wiggleTl.to(this, {
                duration: dur,
                x: x,
                ease: shakeWiggle
            }, 0);
        }
        wiggleTl.to(this, {
            duration: dur,
            y: y,
            ease: shakeWiggle
        }, 0);
        wiggleTl.to(this, {
            x: 0,
            y: 0,
            duration: 0.01
        });

        return wiggleTl;
    }

    public makeNudgeScreenAtTop(): void {
        this.addChild(this._nudgeCounterScreen);
    }

    public showMultiplier(valueText: string): void {
        if (this._nudgeMultiplierText) {
            this._nudgeMultiplierText.destroy();
            this._nudgeMultiplierText = null;
        }
        this._nudgeMultiplierText = new PIXI.Text(valueText, new PIXI.TextStyle(
            {
                fill: "#14E6ED",
                fontFamily: "ARCADECLASSIC",
                wordWrap: false,
                fontWeight: FontWeight.BOLD,
                fontSize: 40,
                letterSpacing: 2,
            }));

        this._nudgeMultiplierText.name = "totalNudgeMultiplier";
        this.onOrientationChanged();
        this._nudgeCounterScreen.addChild(this._nudgeMultiplierText);

        this.currentTotalMultiplierValue = valueText === "" ? 0 : parseInt(valueText.slice(0, valueText.length - 1));
        this._textShowing = true;

    }

    public showWinningStickyXNMultiOnScreen(): void {
        let setCurrentValue: number = 0;

        //either show winning sticky XN 's mutliplier sum at full opacity
        BruteForce.model.isXNPartOfWin.forEach((winNudge: boolean, reelId: number) => {
            if (winNudge && !BruteForce.model.toNudge[reelId]) {
                setCurrentValue += BruteForce.model.xNudgeMultipliers[reelId];
            }
        });
        // or show already sticky XN's multiplier sum faded , not including the new ones
        if (setCurrentValue === 0) {
            this._nudgeMultiplierValue = 0;
        } else {
            this.fadeNudgeMultiplier(setCurrentValue, 1);
        }
    }

    public changeMultiplier(addValue: number, toAdd: boolean): gsap.core.Timeline {
        const incrementTl: gsap.core.Timeline = gsap.timeline();
        let currentValue: number = JSON.parse(JSON.stringify(this._nudgeMultiplierValue));

        for (let i: number = 0; i <= addValue; i++) {
            const singleUpdate: gsap.core.Timeline = gsap.timeline();
            singleUpdate.add(() => {
                if (currentValue + (toAdd ? i : -i) <= 0) {
                    this._nudgeMultiplierText!.text = "";
                } else {
                    this._nudgeMultiplierText!.text = (currentValue + (toAdd ? i : -i)).toString() + "x";
                    this._nudgeMultiplierText!.pivot.x = this._nudgeMultiplierText!.width * 0.5;
                }
            }, 0);
            if (i !== 0) {
                this._nudgeMultiplierValue = this._nudgeMultiplierValue + (toAdd ? 1 : -1);
            }
            incrementTl.add(singleUpdate);
            // incrementTl.to(this, {duration: 0.02});
        }

        return incrementTl;
    }

    public fadeNudgeMultiplier(finalCount: number, value: number): void {
        if (this._textShowing) {
            this._nudgeMultiplierText!.text = finalCount.toString() + "x";
            this._nudgeMultiplierText!.alpha = finalCount === 0 ? 0 : value;
            this.currentTotalMultiplierValue = finalCount;
            this.onOrientationChanged();
        }
    }

    public setAlphaForNudgeMultiplier(value: number): void {
        if (this._textShowing) {
            this._nudgeMultiplierValue !== 0 ? this._nudgeMultiplierText!.alpha = value : this._nudgeMultiplierText!.alpha = 0;
        }
    }

    public resetNudgeMultiplier(): void {
        this._nudgeMultiplierValue = 0;
        if (this._nudgeMultiplierText) {
            this._nudgeMultiplierText.destroy();
            this._nudgeMultiplierText = null;
        }
        this._textShowing = false;
    }

    public getNudgeMultiplierFlying(typeOfNudge: number, currentReel: number, finalCount: number): gsap.core.Timeline {
        const flyTl: gsap.core.Timeline = gsap.timeline();
        const copyMultiContainer: PIXI.Container = new PIXI.Container();
        copyMultiContainer.name = `XNMultipliercopyContainer_${currentReel}`;
        const textStyle: PIXI.TextStyle = new PIXI.TextStyle({
            fill: typeOfNudge === 1 ? "#dac014" : typeOfNudge === 2 ? "#f55b08" : "#ef162e",
            fontFamily: "AMCAP Eternal",
            wordWrap: false,
            fontWeight: FontWeight.BOLD,
            fontSize: 40,
            letterSpacing: 3,
        }); // using fontsize 48 because in nudgesymbol and its assets are being scaled in reelarea
        const copyMultiplier: PIXI.Text = new PIXI.Text(finalCount.toString(), textStyle);
        copyMultiplier.name = "copyMultiplier";// 334 487
        copyMultiContainer.position.set(BruteForceGameConfig.reelSetPositionX + BruteForceGameConfig.symbolWidth * currentReel, 485);
        copyMultiContainer.scale.set(1.2, 1.2);
        copyMultiplier.position.x = (currentReel - 1) * 20;
        //using hardcode values because things are being scaled , not a good practice
        const copyMultiX: PIXI.Text = new PIXI.Text("x", textStyle);
        copyMultiX.name = "copyX";
        copyMultiX.scale.set(0.8, 0.8);
        copyMultiplier.pivot.x = copyMultiplier.width * 0.5;
        copyMultiX.position.set(copyMultiplier.position.x + copyMultiplier.width + 2, copyMultiplier.position.y + 9);
        copyMultiX.pivot.x = copyMultiplier.width * 0.5;

        const trail: PIXI.Sprite = new PIXI.Sprite(BruteForce.assetLoader.getTextures("nudgeMultiFlyTrail")[0]);
        trail.anchor.set(0, 0.5);
        trail.name = "trail";
        trail.position.set(NolimitApplication.isLandscape ? (-10 + (currentReel - 1) * 10) : (currentReel - 1) * 5, 0);
        trail.scale.set(0.7, 0.7);
        trail.blendMode = BLEND_MODES.ADD;
        //add code to change trail rotation
        trail.rotation = NolimitApplication.isLandscape ? this._trailLandscapeRotation[currentReel] : this._trailPortraitRotation[currentReel];

        copyMultiContainer.addChild(trail, copyMultiplier, copyMultiX);
        flyTl.add(() => {
            this.addChild(copyMultiContainer);
        });
        flyTl.add(() => {
            gsap.to(copyMultiContainer, {
                duration: 0.05 * currentReel + 0.25,
                x: NolimitApplication.isLandscape ? -120 - (currentReel - 1) * 25 : 55 - (currentReel - 1) * 20,
                y: NolimitApplication.isLandscape ? 195 : -60
            });
        }, ">");

        flyTl.add(() => {
            BruteForceSound.playPointsToCounter();
        }, "<");

        flyTl.add(() => {
            trail.rotation = NolimitApplication.isLandscape ? this._trailLandscapeRotation[currentReel] : this._trailPortraitRotation[currentReel];
            gsap.to(trail.scale, {x: 0.5, y: 0.5, duration: (0.05 * currentReel + 0.25) / 4});

        }, `>${(0.05 * currentReel + 0.25) / 4}`);
        flyTl.add(() => {
            gsap.to(trail.scale, {x: 1, y: 1, duration: (0.05 * currentReel + 0.25) / 4});

        }, `>${(0.05 * currentReel + 0.25) / 4}`);
        flyTl.add(() => {
            gsap.to(trail.scale, {x: 0.5, y: 0.5, duration: (0.05 * currentReel + 0.25) / 4});

        }, `>${(0.05 * currentReel + 0.25) / 4}`);
        flyTl.add(() => {
            gsap.to(trail.scale, {x: 0.5, y: 0.5, duration: (0.05 * currentReel + 0.25) / 4});

        }, `>${(0.05 * currentReel + 0.25) / 4}`);


        flyTl.add(() => {
            BruteForceSound.playNudgeCounterBlast();
            this.nudgeCounterScreenBlast();
            this.removeChild(copyMultiContainer);
            copyMultiplier.destroy();
            copyMultiX.destroy();
            trail.destroy();
            if (typeOfNudge === 2) {
                this._nudgeCounterScreen.state.setAnimation(0, "level1", false).timeScale = 3 - (0.15 * currentReel);

            } else if (typeOfNudge === 3) {
                this._nudgeCounterScreen.state.setAnimation(0, "level2", false).timeScale = 3 - (0.15 * currentReel);
            }
        }, ">");

        //console.log(flyTl.duration());
        return flyTl;
    }

    public updateNudgeScreenState() {
        this.onOrientationChanged();

    }

    private nudgeCounterScreenBlast(): gsap.core.Timeline {
        const sparkBlastTl: gsap.core.Timeline = gsap.timeline();

        const blast: TimelineSprite = new TimelineSprite(BruteForce.assetLoader.getTextures("nudgeCounterScreenSparks"))
        blast.blendMode = BLEND_MODES.ADD;
        blast.anchor.set(0.5, 0.5);
        blast.name = "nudgeScreenBlast";
        blast.position.set(4, -10);

        sparkBlastTl.add(() => {
            this._nudgeCounterScreen.addChild(blast);
        }, 0);
        sparkBlastTl.add(<gsap.core.Timeline><unknown>blast.getAnimationAutoShowHide(), 0);
        sparkBlastTl.add(() => {
            blast.destroy();
            this._nudgeCounterScreen.removeChild(blast);
        }, blast.totalDuration);
        return sparkBlastTl;
    }

    private isBoosterOn(): boolean {
        const activeBetFeature = BruteForce.apiPlugin.betFeatureController.getActiveBetFeature();
        return activeBetFeature?.name == "GUARANTEED_X_NUDGE" || activeBetFeature?.name == "GUARANTEED_X_NUDGE_2" || activeBetFeature?.name == "GUARANTEED_X_NUDGE_4";

    }
}